import React from 'react';
import clsx from 'clsx';

import Icon from '../Icon';
import EmptyMessage from '../EmptyMessage';
import { getTdClassName } from './getTdClassName';
import { getRoundedClassName } from './getRoundedClassName';

export type ColumnType<T> = {
  header: React.ReactNode;
  headerStyle?: React.CSSProperties;

  sortName?: string;
  getValue: (row: T, index: number) => React.ReactNode;

  truncate?: boolean;
  cellStyle?: React.CSSProperties;
  cellClassName?: string;
  cellContentClassName?: string;
  align?: 'left' | 'right' | 'center';
};

export type ConfigType<T> = {
  columns: ColumnType<T>[];
};

type TableProps<T = any> = {
  data: T[];
  loading?: boolean;
  className?: string;
  clampCells?: boolean;
  withHeader?: boolean;
  config: ConfigType<T>;
  emptyMessage?: string;
  cellsContentClassName?: string;
  verticalAlign?: 'top' | 'middle';
  onRowClick?: (row: T) => void;
  cellsClassName?: string;
  headersClassName?: string;

  sort?: SortState;
  onSort?: (newSort: string) => void;
};

const TableHeader = <T,>({
  sort,
  onSort,
  config,

  headersClassName,
}: Pick<
  TableProps<T>,
  'config' | 'sort' | 'onSort' | 'headersClassName'
> & {}) => {
  return (
    <thead>
      <tr className={clsx(headersClassName)}>
        {config.columns.map((column, index) => {
          const { name, direction: dir } = sort ?? {};
          const sortName = column.sortName;
          const showSort = !!sortName && name === sortName && !!dir;
          const iconElement = <Icon icon={dir === 'asc' ? 'up' : 'down'} />;
          const commonClassName = getRoundedClassName({
            columnIndex: index,
            columnsCount: config.columns.length,
          });

          return (
            <th
              key={index}
              style={column.headerStyle}
              className={clsx(
                'bg-light text-textLight font-medium truncate p-0 m-0 overflow-hidden border-0',
                'uppercase',
                commonClassName,
                column.align === 'right'
                  ? 'text-right'
                  : column.align === 'center'
                    ? 'text-center'
                    : 'text-left'
              )}
            >
              <div
                className={clsx(
                  commonClassName,
                  sortName ? 'cursor-pointer' : '',
                  'flex gap-x-xs w-full py-md px-xl',
                  column.align === 'right' && 'justify-end',
                  column.align === 'center' && 'justify-center',

                  (!column.align || column.align === 'left') && 'justify-start'
                )}
                onClick={sortName ? () => onSort?.(sortName) : undefined}
              >
                {column.header}
                {showSort && iconElement}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const TableRows = <T,>({
  data,
  config,
  clampCells,
  cellsClassName,
  cellsContentClassName,

  verticalAlign = 'middle',
  onRowClick,
}: Pick<
  TableProps<T>,
  | 'data'
  | 'clampCells'
  | 'config'
  | 'cellsClassName'
  | 'cellsContentClassName'
  | 'verticalAlign'
  | 'onRowClick'
>) => {
  return (
    <>
      {data?.map((row, rowIndex) => {
        return (
          <React.Fragment key={rowIndex}>
            <tr
              onClick={e => {
                e.stopPropagation();
                onRowClick?.(row);
              }}
              className={clsx(
                'bg-content hover:bg-bg transition-colors group/tablerow',
                onRowClick ? 'cursor-pointer' : '',
                cellsClassName
              )}
            >
              {config.columns.map((column, index) => {
                const tdClassName = clsx(
                  getTdClassName(column, verticalAlign),
                  getRoundedClassName({
                    columnIndex: index,
                    columnsCount: config.columns.length,
                    rowIndex,
                    rowsCount: data.length || 0,
                  }),
                  'p-0 py-lg',
                  {
                    'text-right': column.align === 'right',
                    'text-center': column.align === 'center',
                    'text-left': !column.align || column.align === 'left',
                  }
                );

                return (
                  <td
                    key={index}
                    style={column.cellStyle}
                    className={tdClassName}
                  >
                    <div
                      className={clsx(
                        'px-xl',

                        column.truncate
                          ? 'truncate'
                          : clampCells && 'line-clamp-4 break-words',
                        cellsContentClassName,
                        column.cellContentClassName,
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right',
                        (!column.align || column.align === 'left') &&
                          'text-left'
                      )}
                    >
                      {column.getValue(row, rowIndex)}
                    </div>
                  </td>
                );
              })}
            </tr>
          </React.Fragment>
        );
      })}
    </>
  );
};

type TableLoaderProps = {
  rowsCount?: number;
  columnsCount: number;
};

const TableLoader: React.FC<TableLoaderProps> = ({
  rowsCount = 4,
  columnsCount,
}) => {
  return (
    <>
      {Array.from({ length: rowsCount }).map((_item, index) => (
        <React.Fragment key={index}>
          <tr>
            <td
              colSpan={columnsCount}
              className={clsx(
                'px-0 overflow-hidden',
                getRoundedClassName({
                  columnIndex: 0,
                  columnsCount: 1,
                  rowsCount,
                  rowIndex: index,
                })
              )}
            >
              <div className="py-8 px-4 bg-light animate-pulse">
                <div className="h-10 bg-gray-500 opacity-50 rounded-lg" />
              </div>
            </td>
          </tr>
        </React.Fragment>
      ))}
    </>
  );
};

const Table = <T,>({
  data = [],
  config,
  className,
  loading = false,
  withHeader = true,
  clampCells = true,
  cellsClassName,
  headersClassName,
  cellsContentClassName,
  verticalAlign = 'middle',
  emptyMessage = 'No items.',
  onRowClick,
  sort,
  onSort,
}: TableProps<T>): React.ReactNode => {
  if (!data?.length && !!emptyMessage && !loading) {
    return <EmptyMessage>{emptyMessage}</EmptyMessage>;
  }

  return (
    <table
      className={clsx(
        className,
        'w-full border-separate border-spacing-0 h-fit',
        'border border-active rounded-sm'
      )}
    >
      {!!withHeader && (
        <TableHeader
          sort={sort}
          onSort={onSort}
          config={config}
          headersClassName={headersClassName}
        />
      )}
      <tbody>
        {loading ? (
          <TableLoader columnsCount={config.columns.length} />
        ) : (
          <TableRows
            data={data}
            config={config}
            clampCells={clampCells}
            verticalAlign={verticalAlign}
            cellsClassName={cellsClassName}
            cellsContentClassName={cellsContentClassName}
            onRowClick={onRowClick}
          />
        )}
      </tbody>
    </table>
  );
};

export default React.memo(Table) as typeof Table;
